<script lang="ts">
  import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
  import { type EmblaOptionsType } from "embla-carousel";
  import emblaCarouselSvelte, {
    type EmblaCarouselSvelteType,
  } from "embla-carousel-svelte";
  import Icon from "@elements/Icon.svelte";
  import { onDestroy } from "svelte";

  /** Props */
  export let startIndex: number = 0;

  /** State */
  let plugins = [WheelGesturesPlugin()];
  let embla: any;
  let isHovering = false;
  let canSlideNext = false;
  let canSlidePrev = false;
  let innerWidth = 0;

  /** Computed */
  $: options = {
    skipSnaps: true,
    active: isScrollable,
    startIndex,
  } as EmblaOptionsType;
  $: slidesOffset =
    innerWidth > 1488.01 + 24 ? (innerWidth - 1536 + 24) / 2 : 0;
  $: isScrollable = false;
  $: isScrollNextVisible = isScrollable && isHovering && canSlideNext;
  $: isScrollPrevVisible = isScrollable && isHovering && canSlidePrev;

  function onInit(event: CustomEvent<EmblaCarouselSvelteType>) {
    embla = event.detail;
    updateScrollableStatus();
    embla.on("select", (e: any) => {
      canSlideNext = embla.canScrollNext();
      canSlidePrev = embla.canScrollPrev();
    });
  }

  function updateScrollableStatus() {
    canSlideNext = embla.canScrollNext();
    canSlidePrev = embla.canScrollPrev();
    isScrollable = canSlideNext || canSlidePrev;
  }

  function scrollPrev() {
    embla.scrollPrev();
  }
  function scrollNext() {
    embla.scrollNext();
  }
  function onMouseEnter() {
    updateScrollableStatus();
    isHovering = true;
  }
  function onMouseLeave() {
    isHovering = false;
  }
  onDestroy(() => {
    if (embla) {
      embla.destroy();
    }
  });
</script>

<svelte:window bind:innerWidth />

<div
  class="embla pb-16 overflow-hidden relative"
  style={`--embla-offset: ${slidesOffset}px`}
>
  <div
    class="embla__viewport"
    role="region"
    aria-label="Carousel"
    use:emblaCarouselSvelte={{ options, plugins }}
    on:emblaInit={onInit}
    on:mouseenter={onMouseEnter}
    on:mouseleave={onMouseLeave}
  >
    <div class="embla__container">
      <slot />
    </div>
  </div>
  {#if canSlidePrev}
    <button
      on:mouseenter={onMouseEnter}
      on:mouseleave={onMouseLeave}
      on:click={scrollPrev}
      aria-label="Previous slide"
      class={`${isScrollPrevVisible ? "opacity-100" : "opacity-0"} hidden md:block transition-all duration-300 embla__prev absolute h-full w-14 bg-black/30 top-0 left-0 stroke-white`}
    >
      <span class="sr-only">Prev</span>
      <Icon name="chevron-left" size="100%" />
    </button>
  {/if}
  {#if canSlideNext}
    <button
      on:mouseenter={onMouseEnter}
      on:mouseleave={onMouseLeave}
      on:click={scrollNext}
      aria-label="Next slide"
      class={`${isScrollNextVisible ? "opacity-100" : "opacity-0"}  hidden md:block transition-all duration-300 embla__next absolute h-full w-14 bg-black/30 top-0 right-0 stroke-white`}
    >
      <span class="sr-only">Next</span>
      <Icon name="chevron-right" size="100%" />
    </button>
  {/if}
</div>

<style>
  .embla {
    --slide-height: 19rem;
    --slide-spacing: 1rem;
    --slide-gap: 1rem;
  }
  .embla__container {
    display: grid;
    grid-auto-flow: column;
    gap: var(--slide-gap);
    grid-auto-columns: min-content;
    touch-action: pan-y;
    backface-visibility: hidden;
    margin-left: var(--embla-offset);
    padding-top: 2rem;
  }

  :global(.embla__slide) {
    width: 400px;
    max-width: 70vw;
  }
  .embla__viewport {
    width: calc(100%);
  }
  /* @media (min-width: 1536px) {
    .embla {
      --slide-size: 25%;
    }
  } */
  @media (max-width: 1535px) {
    .embla__container {
      padding-left: 2rem;
    }

    /* .embla {
      --slide-size: 25%;
    } */
  }
  /* @media (max-width: 900px) {
    .embla {
      --slide-size: 40%;
    }
  } */
  /* @media (max-width: 768px) {
    .embla {
      --slide-size: 60%;
    }
  }
  @media (max-width: 550px) {
    .embla {
      --slide-size: 70%;
    }
  } */
  /* commenting this out to try and fix mobile issue. */
  /* @media (max-width: 411px) {
    .embla {
      --slide-size: calc(85%-1rem);
      --slide-gap: 1rem;
      --slide-spacing: 0;
    }
  } */
</style>
